<template>
  <div v-if="currentSlide">
    <video-player
      v-if="currentVideoId"
      ref="videoPlayer"
      :video-id="currentVideoId"
      @onEndedVideo="onFinalizedVideo"
      @onTimeupdateVideo="onTimeUpdateVideo"
    ></video-player>
    <div v-if="currentVideoId && currentLabels.length">
      <p
        v-for="(label, index) in currentLabels"
        :key="'label-' + index"
        class="label"
        :style="{ top: label.top, left: label.left }"
        tabindex="0"
      >
        {{ label.text }}
      </p>
    </div>
    <div class="description-container">
      <div class="tts">
        <voice-over
          bg-color="white"
          size="2em"
          color-icon="#01E47A"
          :src="require(`@/assets/audios/vo/${currentVoiceOver}.mp3`)"
          @on-play="onPlayVoiceOver"
          @on-complete="onCompleteVoiceOver"
        ></voice-over>
        <p class="tts__message" tabindex="0">{{ currentDescription }}</p>
      </div>
      <slide-modal
        :active-button="activeButton"
        :current-slide="currentSlide"
        @on-button-next="onButtonNext"
        @on-hide-modal="onHideModal"
        ref="slideModal"
      ></slide-modal>
    </div>
    <loading-modal :is-loading="isLoading"></loading-modal>
  </div>
</template>

<script>
import { computed, reactive, ref, toRefs } from '@vue/composition-api';
import { VideoPlayer, VoiceOver } from '@discoveryeducation/components';
import { LoadingModal, SlideModal } from '../modals';

export default {
  name: 'SlideBase',
  components: {
    SlideModal,
    LoadingModal,
    VideoPlayer,
    VoiceOver,
  },
  props: {
    currentSlide: {
      type: Object,
      required: true,
      default: () => {},
    },
    isLoading: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { root, emit }) {
    const { currentSlide } = toRefs(props);
    const currentVideoId = ref(null);
    const videoPlayer = ref(null);
    const activeButton = ref(true);
    const slideModal = ref(null);
    const hasTextChanged = ref(false);
    const state = reactive({
      currentTime: 0,
      videoPaused: false,
      audioStarted: false,
    });

    const stateVoiceOver = reactive({
      start: false,
      complete: false,
    });

    const vol = 0.5;
    const audio = root.getAudioById('bg-sound');

    const isFirstSlide = computed(
      () => currentSlide.value.component === 'Slide0',
    );

    const onButtonNext = () => {
      if (!currentVideoId.value) {
        currentVideoId.value = currentSlide.value.video.id;
        state.currentTime = 0;
        state.videoPaused = false;
        if (currentSlide.value.playAudio) {
          audio.play(true, vol);
        }
      } else {
        isFirstSlide.value
          ? !hasTextChanged.value
            ? (hasTextChanged.value = true)
            : emit('on-next-slide')
          : slideModal.value.showModal();
      }

      activeButton.value = isFirstSlide.value && hasTextChanged.value;
    };

    const onHideModal = ({ isNext }) => {
      currentVideoId.value = null;
      activeButton.value = true;
      if (isNext) emit('on-next-slide');
    };

    const onFinalizedVideo = () => {
      audio.fade(vol, 0, 800);
      audio.audio.on('fade', () => audio.stop());

      if (isFirstSlide.value) {
        if (!stateVoiceOver.start) {
          emit('on-next-slide');
        }
      } else {
        activeButton.value = true;
      }
    };

    const onTimeUpdateVideo = (video) => {
      state.currentTime = Number(video.currentTime).toFixed(2);

      if (isFirstSlide.value) {
        if (state.currentTime > 7.3) {
          if (!state.audioStarted) {
            audio.play(true, vol);
            state.audioStarted = true;
          }
          activeButton.value = true;
          if (state.currentTime >= 20 && !hasTextChanged.value)
            hasTextChanged.value = true;
        }

        if (state.currentTime > 29.8 && stateVoiceOver.start) {
          videoPlayer.value.pauseVideo();
          state.videoPaused = true;
        }
      }
    };

    const description = computed(() => {
      return currentVideoId.value
        ? currentSlide.value.descriptions
            .filter(
              (description) =>
                description.timeInit <= state.currentTime &&
                state.currentTime <= description.timeEnd,
            )
            .map((description) => description.text)
            .shift()
        : currentSlide.value.descriptions[0].text;
    });

    const voiceOver = computed(() => {
      return currentVideoId.value
        ? currentSlide.value.descriptions
            .filter(
              (vo) =>
                vo.timeInit <= state.currentTime &&
                state.currentTime <= vo.timeEnd,
            )
            .map((vo) => vo.voiceOver)
            .shift()
        : currentSlide.value.descriptions[0].voiceOver;
    });

    const currentDescription = computed(() => {
      if (isFirstSlide.value) {
        return !hasTextChanged.value
          ? description.value
          : currentSlide.value.descriptions[2].text;
      } else {
        return description.value;
      }
    });

    const currentVoiceOver = computed(() => {
      if (isFirstSlide.value) {
        return !hasTextChanged.value
          ? voiceOver.value
          : currentSlide.value.descriptions[2].voiceOver;
      } else {
        return voiceOver.value;
      }
    });

    const currentLabels = computed(() => {
      if (!currentSlide.value.labels) return [];
      return currentSlide.value.labels.filter(
        (label) =>
          label.timeInit <= state.currentTime &&
          state.currentTime <= label.timeEnd,
      );
    });

    const onPlayVoiceOver = () => {
      stateVoiceOver.complete = false;
      stateVoiceOver.start = true;
    };

    const onCompleteVoiceOver = () => {
      if (
        videoPlayer.value &&
        !videoPlayer.value.playing &&
        isFirstSlide.value
      ) {
        videoPlayer.value.playVideo();
      }
      stateVoiceOver.complete = true;
      stateVoiceOver.start = false;
    };

    return {
      currentVideoId,
      videoPlayer,
      activeButton,
      slideModal,
      onButtonNext,
      onHideModal,
      onFinalizedVideo,
      onTimeUpdateVideo,
      currentLabels,
      currentDescription,
      currentVoiceOver,
      onPlayVoiceOver,
      onCompleteVoiceOver,
    };
  },
};
</script>

<style lang="stylus" scoped>
color-white = colors.white;
color-green-200 = colors.green-200;

.label
  position absolute
  font-size 42px
  line-height 57px
  z-index 5
  animation fadeIn 1s forwards

.description-container
  position absolute
  bottom 39px
  left 0
  right 0
  margin 0 auto
  width 1466px
  height 116px
  border-radius radius-card
  background-color color-white
  padding 5px 44px 5px 40px
  z-index 5
  display flex

  .tts
    display flex
    align-items center
    width 1360px
    height 90px
    padding-top 20px
    z-index 2

    &__message
      font-size 22px
      line-height 30px
      width 1055px

    ::v-deep
      .button--play-audio
        padding 0
        margin-right 32px

        &:hover
          svg
            fill color-green-200 !important


.wrapper-slide-modal
  display flex
  align-items center

.video-wrapper
  z-index 1

@keyframes fadeIn {
  from {
    opacity 0
  }
  to {
    opacity 1
  }
}
</style>
